"use client"

import { useEffect, useState } from "react"
import { connect } from "react-redux"
import { changePlay } from "@/core/actions"

import { db } from "@/app/pages/offlinePage/db/db"
import TextBoldL from "@/components/text/text-bold-l"
import TextRegularM from "@/components/text/text-regular-m"

import Playgif from "@/image/now-play.webp"
import * as Icons from "@/components/icons"

type Song = {
  id: string
  songName: string
  songimg: string
  songArtist: string
  link: string
  trackTime?: string
}

type Props = {
  data: {
    song: Song
    listType?: "album" | "playlist"
  }
  isPlaying: boolean
  trackData: any
  changePlay: (value: boolean) => void
}

function AlbumTrack({
  data,
  isPlaying,
  trackData,
  changePlay,
}: Props) {
  const [thisSong, setThisSong] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [isOfflineSaved, setIsOfflineSaved] = useState(false)

  /* =========================================================
      CHECK CURRENT SONG
  ========================================================= */

  useEffect(() => {
    setThisSong(
      data.song.link === trackData?.track && isPlaying
    )
  }, [data.song.link, trackData?.track, isPlaying])

  /* =========================================================
      CHECK OFFLINE
  ========================================================= */

  useEffect(() => {
    const check = async () => {
      const saved = await db.songs.get(data.song.link)
      if (saved?.offline === "si") setIsOfflineSaved(true)
    }

    check()
  }, [data.song.link])

  /* =========================================================
      SAVE OFFLINE
  ========================================================= */

  const confirmSaveOffline = async () => {
    setShowModal(false)

    try {
      const res = await fetch(data.song.link)
      const blob = await res.blob()

      await db.songs.put({
        url: data.song.link,
        name: data.song.songName,
        artist: data.song.songArtist,
        songimg: data.song.songimg,
        blob,
        offline: "si",
        savedAt: new Date(),
      })

      setIsOfflineSaved(true)
    } catch (err) {
      console.error(err)
    }
  }

  /* =========================================================
      UI
  ========================================================= */

  return (
    <>
    <div
        className={`
          relative
          grid
          items-center
          h-15
          w-full
          rounded-lg
          px-2
          lg:px-4
          gap-2
          lg:gap-4
          transition-all

          ${
            data.listType ===
            "album"
              ? "grid-cols-[1.5rem_1fr_3rem_3rem]"
              : "grid-cols-[1.5rem_1fr_3rem_3rem]"
          }

          hover:bg-white/[0.03]
          active:bg-white/[0.10]
        `}
      >
      {/* PLAY BUTTON (overlay hover) */}
      <button
        onClick={() => changePlay(!isPlaying)}
        className="
          absolute left-0
          w-10 h-10
          flex items-center justify-center
          opacity-0
          hover:opacity-100
          transition
        "
      >
        {thisSong ? <Icons.Pause /> : <Icons.Play />}
      </button>

      {/* INDEX / GIF */}
      {thisSong ? (
        <img
          src={Playgif}
          className="w-3 mx-auto filter hue-rotate-90"
          alt="playing"
        />
      ) : (
        <p className="text-center text-white/70 text-sm">
          {data.song.id}
        </p>
      )}

      {/* IMAGE */}
      {data.listType !== "album" && (
        <img
          src={data.song.songimg}
          className="w-10 h-10 rounded-md object-cover"
        />
      )}

      {/* TEXT */}
      <div className="flex flex-col justify-center min-w-0">
        <TextBoldL>{data.song.songName}</TextBoldL>
        <TextRegularM>{data.song.songArtist}</TextRegularM>
      </div>

      {/* OFFLINE BUTTON */}
      <button
        onClick={() => setShowModal(true)}
        className="text-white/60 hover:text-white transition"
      >
        <i className="f7-icons text-xl">
          {isOfflineSaved
            ? "arrow_down_circle_fill"
            : "arrow_down_circle"}
        </i>
      </button>

      {/* TIME */}
      <p className="text-white/60 text-sm text-center">
        {data.song.trackTime}
      </p>

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[9999]">
          <div className="bg-black p-4 rounded-xl text-center w-72">
            <p className="text-white">¿Guardar en Offline?</p>

            <div className="grid grid-cols-2 gap-2 mt-4">
              <button
                onClick={confirmSaveOffline}
                className="bg-blue-500 hover:bg-blue-400 text-white rounded-md py-2"
              >
                Guardar
              </button>

              <button
                onClick={() => setShowModal(false)}
                className="bg-white/20 hover:bg-white/30 text-white rounded-md py-2"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  )
}

/* =========================================================
    REDUX
========================================================= */

const mapStateToProps = (state: any) => ({
  isPlaying: state.isPlaying,
  trackData: state.trackData,
})

export default connect(mapStateToProps, { changePlay })(
  AlbumTrack
)